import SpiritoryModal from '@/components/common/modals/SpiritoryModal';
import {
  InitModalData,
  SpiritoryModalContextProps,
  SpiritoryModalModalType,
} from '@/types/context';
import { usePathname } from 'next/navigation';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';

const SpiritoryModalContext = createContext<
  SpiritoryModalContextProps | undefined
>(undefined);

export const SpiritoryModalProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [previousPathname, setPreviousPathname] = useState<string>('');
  const pathname = usePathname();
  const [showModal, setShowModal] = useState(false);
  const [cancelAction, setCancelAction] = useState<() => void>(() => () => {});
  const [submitAction, setSubmitAction] = useState<() => void>(() => () => {});
  const [submitActionDisabled, setSubmitActionDisabled] = useState(false);
  const [cancelActionDisabled, setCancelActionDisabled] = useState(false);
  const [title, setTitle] = useState<React.ReactNode>('');
  const [body, setBody] = useState<ReactNode>(null);
  const [cancelText, setCancelText] = useState<string | undefined>(undefined);
  const [submitText, setSubmitText] = useState<React.ReactNode | undefined>(
    undefined
  );
  const [showButtons, setShowButtons] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [showSubmitButton, setShowSubmitButton] = useState(true);
  const [modalType, setModalType] = useState<SpiritoryModalModalType>('normal');

  const handleClose = () => {
    cancelAction();

    setShowModal(false);

    setTimeout(() => {
      setTitle('');
      setBody(null);
      setCancelText(undefined);
      setSubmitText(undefined);
      setShowButtons(true);
      setShowCancelButton(true);
      setShowSubmitButton(true);
      setSubmitActionDisabled(false);
      setCancelActionDisabled(false);
      setCancelAction(() => () => {});
      setSubmitAction(() => () => {});
      setModalType('normal');
    }, 150);
  };

  const handleNormalClose = () => {
    setShowModal(false);

    setTimeout(() => {
      setTitle('');
      setBody(null);
      setCancelText(undefined);
      setSubmitText(undefined);
      setShowButtons(true);
      setShowCancelButton(true);
      setShowSubmitButton(true);
      setSubmitActionDisabled(false);
      setCancelActionDisabled(false);
      setCancelAction(() => () => {});
      setSubmitAction(() => () => {});
      setModalType('normal');
    }, 150);
  };
  const handleOpen = () => {
    setShowModal(true);
  };

  const handleSubmit = () => {
    submitAction();
    handleNormalClose();
  };

  const initModal = (data: InitModalData) => {
    const {
      title,
      body,
      type = 'normal',
      submitAction,
      submitText,
      cancelText,
      showCancelButton = true,
      showSubmitButton = true,
      showButtons = true,
      cancelAction,
      openOnInit = true,
    } = data;

    setTitle(title);
    setBody(body);
    setModalType(type);
    setSubmitAction(() => submitAction);
    setSubmitText(submitText);
    setCancelText(cancelText);
    setShowCancelButton(showCancelButton);
    setShowSubmitButton(showSubmitButton);
    setShowButtons(showButtons);

    if (cancelAction) {
      setCancelAction(() => cancelAction);
    }

    if (openOnInit) {
      handleOpen();
    }
  };

  useEffect(() => {
    const handleCloseInEffect = () => {
      cancelAction();

      setShowModal(false);

      setTimeout(() => {
        setTitle('');
        setBody(null);
        setCancelText(undefined);
        setSubmitText(undefined);
        setShowButtons(true);
        setShowCancelButton(true);
        setShowSubmitButton(true);
        setSubmitActionDisabled(false);
        setCancelActionDisabled(false);
        setCancelAction(() => () => {});
        setSubmitAction(() => () => {});
        setModalType('normal');
      }, 150);
    };
    if (Boolean(previousPathname) && previousPathname !== pathname) {
      handleCloseInEffect();
    }
  }, [pathname, previousPathname, cancelAction]);

  useEffect(() => {
    setPreviousPathname(pathname || '');
  }, [pathname]);

  return (
    <SpiritoryModalContext.Provider
      value={{
        showModal,
        closeModal: handleClose,
        openModal: handleOpen,
        setSubmitActionDisabled: (submitActionDisabled: boolean) =>
          setSubmitActionDisabled(submitActionDisabled),
        setCancelActionDisabled: (cancelActionDisabled: boolean) =>
          setCancelActionDisabled(cancelActionDisabled),
        setSubmitAction: (submitAction: () => void) =>
          setSubmitAction(submitAction),
        setCancelAction: (cancelAction: () => void) =>
          setCancelAction(cancelAction),
        setTitle: (title: React.ReactNode) => setTitle(title),
        setBody: (body: ReactNode) => setBody(body),
        setShowCancelButton: (showCancelButton: boolean) =>
          setShowCancelButton(showCancelButton),
        setShowSubmitButton: (showSubmitButton: boolean) =>
          setShowSubmitButton(showSubmitButton),
        setSubmitText: (submitText: React.ReactNode) =>
          setSubmitText(submitText),
        setCancelText: (cancelText: string) => setCancelText(cancelText),
        setShowButtons: (showButtons: boolean) => setShowButtons(showButtons),
        setModalType: (type: SpiritoryModalModalType) => setModalType(type),
        initModal,
      }}
    >
      {children}
      <SpiritoryModal
        showModal={showModal}
        cancelAction={handleClose}
        submitAction={handleSubmit}
        cancelText={cancelText}
        submitText={submitText}
        title={title}
        body={body}
        showButtons={showButtons}
        submitActionDisabled={submitActionDisabled}
        cancelActionDisabled={cancelActionDisabled}
        showCancelButton={showCancelButton}
        showSubmitButton={showSubmitButton}
        type={modalType}
      />
    </SpiritoryModalContext.Provider>
  );
};

export const useSpiritoryModal = () => {
  const context = useContext(SpiritoryModalContext);
  if (!context) {
    throw new Error(
      'useSpiritoryModal must be used within a SpiritoryModalProvider'
    );
  }
  return context;
};
